import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Select,
  Button,
  Typography,
  Progress,
  Tabs,
  Tag,
  Spin,
  Empty,
  Tooltip,
  Pagination,
} from "antd";
import {
  DollarOutlined,
  FileTextOutlined,
  FilterOutlined,
  ScheduleOutlined,
  SafetyCertificateOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { Bar, Pie, Doughnut } from "react-chartjs-2";
import moment from "moment";
import { AppContext } from "../context/AppContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ChartDataLabels from "chartjs-plugin-datalabels";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

// Zambian theme colors based on flag - toned down version
const zambianColors = {
  green: "#69A974", // Softer green
  red: "#E27781",   // Softer red
  orange: "#F5A966", // Softer orange
  black: "#555555",  // Softer black
  white: "#FFFFFF",  // White for contrast
  lightGreen: "#8FBC94", // Lighter green for hover states
  lightOrange: "#FFC080", // Lighter orange for hover states
};

const LoanPortfolioSummary = () => {
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: [moment().startOf("month"), moment().endOf("month")],
    status: "all",
    viewMode: "summary", // New filter: summary or detailed
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { company } = useContext(AppContext);

  const token = localStorage.getItem("token");

  const statusMap = {
    1: {
      text: "Pending Approval",
      color: zambianColors.orange,
      icon: <ExclamationCircleOutlined />,
    },
    2: {
      text: "Under Review",
      color: zambianColors.orange,
      icon: <ExclamationCircleOutlined />,
    },
    3: {
      text: "Incomplete Application",
      color: zambianColors.orange,
      icon: <ExclamationCircleOutlined />,
    },
    7: { text: "Active", color: zambianColors.green, icon: <ScheduleOutlined /> },
    8: { text: "Overdue", color: zambianColors.red, icon: <SafetyCertificateOutlined /> },
    10: { text: "Closed", color: zambianColors.black, icon: <FileTextOutlined /> },
    12: {
      text: "App Application",
      color: "purple",
      icon: <ExclamationCircleOutlined />,
    },
  };

  const statusColors = {
    active: zambianColors.green,
    overdue: zambianColors.red,
    pending: zambianColors.orange,
    closed: zambianColors.black,
  };

  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/loans?fields=*,loan_product.id,loan_product.loan_name,borrower.first_name,borrower.last_name&limit=-1`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              filter: {
                company: company?.id,
                ...(filters.status !== "all" && {
                  loan_status: filters.status,
                }),
              },
              sort: "-id",
            },
          }
        );

        const loans = response.data.data;
        processPortfolioData(loans);
      } catch (error) {
        console.error("Error fetching portfolio data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioData();
  }, [filters]);

  const processPortfolioData = (loans) => {
    // Group loans by status for better handling of large datasets
    const groupedByStatus = {
      active: loans.filter((loan) => loan.loan_status === 7),
      overdue: loans.filter((loan) => loan.loan_status === 8),
      closed: loans.filter((loan) => loan.loan_status === 10),
      pending: loans.filter((loan) => [1, 2, 3].includes(loan.loan_status)),
      app: loans.filter((loan) => loan.loan_status === 12),
    };

    // Group loans by type for better visualization
    const loanTypeGroups = {};
    loans.forEach(loan => {
      const typeName = loan.loan_product.loan_name;
      if (!loanTypeGroups[typeName]) {
        loanTypeGroups[typeName] = {
          count: 0,
          totalAmount: 0,
        };
      }
      loanTypeGroups[typeName].count += 1;
      loanTypeGroups[typeName].totalAmount += loan.amount;
    });

    // For large datasets, we'll aggregate data by month
    const monthlyData = {};
    loans.forEach(loan => {
      const month = moment(loan.date_created).format('YYYY-MM');
      if (!monthlyData[month]) {
        monthlyData[month] = {
          count: 0,
          amount: 0,
          recovered: 0,
        };
      }
      monthlyData[month].count += 1;
      monthlyData[month].amount += loan.amount;
      monthlyData[month].recovered += (loan.paid_amount || 0);
    });

    // Convert to array and sort by date
    const timeSeriesData = Object.entries(monthlyData).map(([month, data]) => ({
      month,
      ...data,
    })).sort((a, b) => moment(a.month).diff(moment(b.month)));

    const metrics = {
      // Store all loans but paginate display
      allLoans: loans,
      // Get last 5 loans for quick view
      recentLoans: loans.slice(-5),
      totalLoans: loans.length,
      totalDisbursed: loans.reduce((sum, loan) => sum + loan.amount, 0),
      totalRecovered: loans.reduce(
        (sum, loan) => sum + (loan.paid_amount || 0),
        0
      ),
      activeLoans: groupedByStatus.active.length,
      overdueLoans: groupedByStatus.overdue.length,
      closedLoans: groupedByStatus.closed.length,
      statusDistribution: {
        active: groupedByStatus.active.length,
        overdue: groupedByStatus.overdue.length,
        closed: groupedByStatus.closed.length,
        pending: groupedByStatus.pending.length,
        app: groupedByStatus.app.length,
      },
      loanTypes: loanTypeGroups,
      timeSeriesData: timeSeriesData,
      // Store loan groups for easier access
      loanGroups: groupedByStatus,
    };

    setPortfolioData(metrics);
  };

  const generatePDFReport = () => {
    // Create a new PDF document
    const doc = new jsPDF();

    // Set up some initial settings
    const marginLeft = 14;
    let currentY = 20;

    // Add a title
    doc.setFontSize(18);
    doc.text("Loan Portfolio Summary", marginLeft, currentY);
    currentY += 10;

    // Add company and date information
    doc.setFontSize(12);
    doc.text(`Company: ${company?.name || "N/A"}`, marginLeft, currentY);
    currentY += 7;
    doc.text(`Date: ${moment().format("MMMM Do YYYY")}`, marginLeft, currentY);
    currentY += 10;

    // Add key metrics
    doc.setFontSize(12);
    doc.text(`Total Loans: ${portfolioData.totalLoans}`, marginLeft, currentY);
    currentY += 7;
    doc.text(
      `Total Disbursed: ${portfolioData.totalDisbursed} ZMW`,
      marginLeft,
      currentY
    );
    currentY += 7;
    doc.text(
      `Total Recovered: ${portfolioData.totalRecovered} ZMW`,
      marginLeft,
      currentY
    );
    currentY += 7;
    doc.text(
      `Active Loans: ${portfolioData.activeLoans}`,
      marginLeft,
      currentY
    );
    currentY += 7;
    doc.text(
      `Overdue Loans: ${portfolioData.overdueLoans}`,
      marginLeft,
      currentY
    );
    currentY += 7;
    doc.text(
      `Closed Loans: ${portfolioData.closedLoans}`,
      marginLeft,
      currentY
    );
    currentY += 7;
    const recoveryRate = (
      (portfolioData.totalRecovered / portfolioData.totalDisbursed) *
      100
    ).toFixed(1);
    doc.text(`Recovery Rate: ${recoveryRate}%`, marginLeft, currentY);
    currentY += 10;

    // Prepare table columns and rows for recent loans
    const tableColumns = [
      "Loan ID",
      "Borrower",
      "Amount (ZMW)",
      "Disbursement Date",
      "Status",
    ];
    const tableRows = portfolioData.recentLoans.map((loan) => {
      const borrower = loan.borrower
        ? `${loan.borrower.first_name} ${loan.borrower.last_name}`
        : "";
      const disbursementDate = moment(loan.disbursement_date).format(
        "DD MMM YYYY"
      );
      const statusText = statusMap[loan.loan_status]
        ? statusMap[loan.loan_status].text
        : loan.loan_status;
      return [loan.id, borrower, loan.amount, disbursementDate, statusText];
    });

    // Add table using autoTable
    doc.autoTable({
      startY: currentY,
      head: [tableColumns],
      body: tableRows,
      theme: "striped",
      styles: { fontSize: 10 },
    });

    // Save the generated PDF
    doc.save("portfolio-summary.pdf");
  };

  const exportToCSV = () => {
    console.log("Exporting to CSV...");
    // Ensure portfolioData and allLoans exist before exporting
    if (!portfolioData || !portfolioData.allLoans) {
      console.error("No data available to export");
      return;
    }

    // Define the CSV headers
    const headers = [
      "Loan ID",
      "Borrower",
      "Amount (ZMW)",
      "Disbursement Date",
      "Status",
    ];

    // Map each loan to a row for the CSV
    const rows = portfolioData.allLoans.map((loan) => {
      // Construct the Borrower field from first and last name
      const borrower = loan.borrower
        ? `${loan.borrower.first_name} ${loan.borrower.last_name}`
        : "";
      // Format the disbursement date using moment
      const disbursementDate = moment(loan.disbursement_date).format(
        "DD MMM YYYY"
      );
      // Get status text from statusMap
      const statusText = statusMap[loan.loan_status]
        ? statusMap[loan.loan_status].text
        : loan.loan_status;

      return [loan.id, borrower, loan.amount, disbursementDate, statusText];
    });

    // Build the CSV content
    let csvContent = "";
    csvContent += headers.join(",") + "\n"; // add headers

    // Add rows to CSV content (wrap values in quotes to handle commas in data)
    rows.forEach((row) => {
      const csvRow = row.map((field) => `"${field}"`).join(",");
      csvContent += csvRow + "\n";
    });

    // Create a Blob from the CSV content and generate a download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "portfolio-summary.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Get current page of loans
  const getCurrentLoans = () => {
    if (!portfolioData?.allLoans) return [];
    const startIndex = (currentPage - 1) * pageSize;
    return portfolioData.allLoans.slice(startIndex, startIndex + pageSize);
  };

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Loading Portfolio Data..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        {/* Header Section */}
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={3} style={{ marginBottom: 0, color: "#555555" }}>
              Loan Portfolio Summary
            </Title>
            <Text type="secondary">
              {company?.name} • As of {moment().format("MMMM Do YYYY")}
            </Text>
          </Col>
          <Col>
            <Button.Group>
              <Button 
                onClick={generatePDFReport} 
                style={{ 
                  backgroundColor: "#f7f7f7", 
                  color: zambianColors.green, 
                  borderColor: "#e0e0e0", 
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)" 
                }}
                icon={<FileTextOutlined />}
              >
                PDF
              </Button>
              <Button 
                onClick={exportToCSV}
                style={{ 
                  backgroundColor: "#f7f7f7", 
                  color: zambianColors.orange, 
                  borderColor: "#e0e0e0", 
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)" 
                }}
                icon={<FileTextOutlined />}
              >
                CSV
              </Button>
            </Button.Group>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Select
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: "100%" }}
            >
              <Option value="all">All Statuses</Option>
              <Option value="7">Active</Option>
              <Option value="8">Overdue</Option>
              <Option value="10">Closed</Option>
              <Option value="1,2,3">Pending</Option>
              <Option value="12">App Application</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Select
              value={filters.viewMode}
              onChange={(value) => setFilters({ ...filters, viewMode: value })}
              style={{ width: "100%" }}
            >
              <Option value="summary">Summary View</Option>
              <Option value="detailed">Detailed View</Option>
            </Select>
          </Col>
        </Row>

        {/* Key Metrics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Card bordered={false} style={{ borderLeft: `2px solid ${zambianColors.green}`, boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)' }}>
              <Statistic
                title="Total Loans"
                value={portfolioData.totalLoans}
                valueStyle={{ color: zambianColors.green, fontSize: 22 }}
              />
              <Progress
                percent={(
                  (portfolioData.activeLoans / portfolioData.totalLoans) *
                  100
                ).toFixed(1)}
                strokeColor={statusColors.active}
                strokeWidth={5}
                showInfo={false}
              />
              <Text type="secondary">
                {portfolioData.activeLoans} Active • {portfolioData.closedLoans}{" "}
                Closed
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Card bordered={false} style={{ borderLeft: `2px solid ${zambianColors.orange}`, boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)' }}>
              <Statistic
                title="Total Disbursed"
                value={portfolioData.totalDisbursed}
                precision={2}
                prefix={<DollarOutlined />}
                valueStyle={{ color: zambianColors.orange, fontSize: 22 }}
                suffix="ZMW"
              />
              <Text type="secondary" style={{ marginTop: 8 }}>
                {portfolioData.totalRecovered.toLocaleString()} ZMW Recovered
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Card bordered={false} style={{ borderLeft: `2px solid ${statusColors.active}`, boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)' }}>
              <Statistic
                title="Active Loans"
                value={portfolioData.activeLoans}
                valueStyle={{ color: statusColors.active, fontSize: 22 }}
              />
              <Text type="secondary" style={{ marginTop: 8 }}>
                {portfolioData.overdueLoans} Overdue
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Card bordered={false} style={{ borderLeft: `2px solid ${zambianColors.black}`, boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)' }}>
              <Statistic
                title="Recovery Rate"
                value={(
                  (portfolioData.totalRecovered /
                    portfolioData.totalDisbursed) *
                  100
                ).toFixed(1)}
                suffix="%"
                valueStyle={{ color: zambianColors.green, fontSize: 22 }}
              />
              <Progress
                percent={(
                  (portfolioData.totalRecovered /
                    portfolioData.totalDisbursed) *
                  100
                ).toFixed(1)}
                status="active"
                strokeColor={zambianColors.green}
                strokeWidth={5}
                showInfo={false}
              />
            </Card>
          </Col>
        </Row>

        {/* Charts Section - Only show in summary mode */}
        {filters.viewMode === "summary" && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12}>
              <Card 
                title="Loans by Status" 
                bordered={false}
                headStyle={{ 
                  backgroundColor: "#f7f7f7", 
                  color: '#555555',
                  borderBottom: `1px solid #eaeaea`,
                  fontWeight: 500
                }}
                style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)' }}
              >
                <Doughnut
                  data={{
                    labels: Object.keys(portfolioData.statusDistribution).map(
                      key => key.charAt(0).toUpperCase() + key.slice(1)
                    ),
                    datasets: [
                      {
                        data: Object.values(portfolioData.statusDistribution),
                        backgroundColor: [
                          statusColors.active,
                          statusColors.overdue,
                          statusColors.closed,
                          statusColors.pending,
                        ],
                        borderColor: 'white',
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { position: "bottom" },
                      tooltip: { 
                        enabled: true,
                        callbacks: {
                          label: function(context) {
                            const total = Object.values(portfolioData.statusDistribution).reduce((a, b) => a + b, 0);
                            const percentage = Math.round((context.raw / total) * 100);
                            return `${context.label}: ${context.raw} (${percentage}%)`;
                          }
                        }
                      },
                      datalabels: {
                        formatter: (value, ctx) => {
                          const total = Object.values(portfolioData.statusDistribution).reduce((a, b) => a + b, 0);
                          const percentage = Math.round((value / total) * 100);
                          return percentage > 5 ? `${percentage}%` : '';
                        },
                        color: '#fff',
                        font: { size: 11, weight: 'bold' },
                      }
                    },
                  }}
                  plugins={[ChartDataLabels]}
                />
                <div style={{ textAlign: 'center', marginTop: 15 }}>
                  <Button 
                    icon={<InfoCircleOutlined />}
                    onClick={() => setFilters({ ...filters, viewMode: 'detailed' })}
                    style={{ border: `1px solid #e0e0e0`, backgroundColor: '#fafafa' }}
                  >
                    View Details
                  </Button>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card 
                title="Monthly Distribution" 
                bordered={false}
                headStyle={{ 
                  backgroundColor: "#f7f7f7", 
                  color: '#555555',
                  borderBottom: `1px solid #eaeaea`,
                  fontWeight: 500
                }}
                style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)' }}
              >
                <Bar
                  data={{
                    labels: portfolioData.timeSeriesData.slice(-6).map(item => 
                      moment(item.month).format('MMM YYYY')
                    ),
                    datasets: [
                      {
                        label: "Loan Amount",
                        data: portfolioData.timeSeriesData.slice(-6).map(item => item.amount),
                        backgroundColor: zambianColors.green,
                        borderColor: zambianColors.green,
                        borderWidth: 1,
                      },
                      {
                        label: "Recovered",
                        data: portfolioData.timeSeriesData.slice(-6).map(item => item.recovered),
                        backgroundColor: zambianColors.orange,
                        borderColor: zambianColors.orange,
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            if (value >= 1000000) {
                              return (value / 1000000).toFixed(1) + 'M';
                            } else if (value >= 1000) {
                              return (value / 1000).toFixed(0) + 'K';
                            }
                            return value;
                          }
                        }
                      }
                    },
                    plugins: {
                      legend: { position: "bottom" },
                      tooltip: { 
                        enabled: true,
                        callbacks: {
                          label: function(context) {
                            let label = context.dataset.label || '';
                            if (label) {
                              label += ': ';
                            }
                            label += new Intl.NumberFormat('en-US', { 
                              style: 'currency', 
                              currency: 'ZMW',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }).format(context.raw);
                            return label;
                          }
                        }
                      },
                    },
                  }}
                />
                <div style={{ textAlign: 'center', marginTop: 15 }}>
                  <Text type="secondary">
                    Showing last 6 months of loan activity
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Detailed View - Only show in detailed mode */}
        {filters.viewMode === "detailed" && (
          <Tabs defaultActiveKey="active" type="card" style={{ marginBottom: 24 }}>
            <TabPane tab={`Active (${portfolioData.activeLoans})`} key="active">
              <Table
                columns={[
                  { title: "Loan ID", dataIndex: "id", key: "id" },
                  {
                    title: "Borrower",
                    dataIndex: ["borrower", "first_name"],
                    render: (_, record) =>
                      record.borrower ? `${record.borrower.first_name} ${record.borrower.last_name}` : "N/A",
                  },
                  {
                    title: "Amount (ZMW)",
                    dataIndex: "amount",
                    render: (value) => value.toLocaleString(),
                    sorter: (a, b) => a.amount - b.amount,
                  },
                  {
                    title: "Disbursement Date",
                    dataIndex: "disbursement_date",
                    render: (date) => moment(date).format("DD MMM YYYY"),
                    sorter: (a, b) => moment(a.disbursement_date).unix() - moment(b.disbursement_date).unix(),
                  },
                  {
                    title: "Loan Type",
                    dataIndex: ["loan_product", "loan_name"],
                    render: (_, record) => record.loan_product?.loan_name || "N/A",
                  },
                  {
                    title: "Status",
                    dataIndex: "loan_status",
                    render: (status) => (
                      <Tag color={statusMap[status]?.color || 'default'}>
                        {statusMap[status]?.text || status}
                      </Tag>
                    ),
                    filters: Object.entries(statusMap).map(([key, value]) => ({
                      text: value.text,
                      value: key,
                    })),
                    onFilter: (value, record) => record.loan_status.toString() === value,
                  },
                ]}
                dataSource={portfolioData.loanGroups.active}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50"],
                }}
              />
            </TabPane>
            <TabPane tab={`Overdue (${portfolioData.overdueLoans})`} key="overdue">
              <Table
                columns={[
                  { title: "Loan ID", dataIndex: "id", key: "id" },
                  {
                    title: "Borrower",
                    dataIndex: ["borrower", "first_name"],
                    render: (_, record) =>
                      record.borrower ? `${record.borrower.first_name} ${record.borrower.last_name}` : "N/A",
                  },
                  {
                    title: "Amount (ZMW)",
                    dataIndex: "amount",
                    render: (value) => value.toLocaleString(),
                    sorter: (a, b) => a.amount - b.amount,
                  },
                  {
                    title: "Disbursement Date",
                    dataIndex: "disbursement_date",
                    render: (date) => moment(date).format("DD MMM YYYY"),
                    sorter: (a, b) => moment(a.disbursement_date).unix() - moment(b.disbursement_date).unix(),
                  },
                  {
                    title: "Loan Type",
                    dataIndex: ["loan_product", "loan_name"],
                    render: (_, record) => record.loan_product?.loan_name || "N/A",
                  },
                  {
                    title: "Days Overdue",
                    dataIndex: "date_created",
                    render: (date) => {
                      const dueDate = moment(date).add(30, 'days'); // Example - assuming 30 day loans
                      return moment().diff(dueDate, 'days');
                    },
                    sorter: (a, b) => {
                      const aDueDate = moment(a.date_created).add(30, 'days');
                      const bDueDate = moment(b.date_created).add(30, 'days');
                      return moment().diff(aDueDate, 'days') - moment().diff(bDueDate, 'days');
                    },
                  },
                ]}
                dataSource={portfolioData.loanGroups.overdue}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50"],
                }}
              />
            </TabPane>
            <TabPane tab={`Closed (${portfolioData.closedLoans})`} key="closed">
              <Table
                columns={[
                  { title: "Loan ID", dataIndex: "id", key: "id" },
                  {
                    title: "Borrower",
                    dataIndex: ["borrower", "first_name"],
                    render: (_, record) =>
                      record.borrower ? `${record.borrower.first_name} ${record.borrower.last_name}` : "N/A",
                  },
                  {
                    title: "Amount (ZMW)",
                    dataIndex: "amount",
                    render: (value) => value.toLocaleString(),
                    sorter: (a, b) => a.amount - b.amount,
                  },
                  {
                    title: "Disbursement Date",
                    dataIndex: "disbursement_date",
                    render: (date) => moment(date).format("DD MMM YYYY"),
                    sorter: (a, b) => moment(a.disbursement_date).unix() - moment(b.disbursement_date).unix(),
                  },
                  {
                    title: "Closed Date",
                    dataIndex: "date_updated",
                    render: (date) => moment(date).format("DD MMM YYYY"),
                    sorter: (a, b) => moment(a.date_updated).unix() - moment(b.date_updated).unix(),
                  },
                  {
                    title: "Loan Type",
                    dataIndex: ["loan_product", "loan_name"],
                    render: (_, record) => record.loan_product?.loan_name || "N/A",
                  },
                ]}
                dataSource={portfolioData.loanGroups.closed}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50"],
                }}
              />
            </TabPane>
            <TabPane tab={`Pending (${portfolioData.statusDistribution.pending})`} key="pending">
              <Table
                columns={[
                  { title: "Loan ID", dataIndex: "id", key: "id" },
                  {
                    title: "Borrower",
                    dataIndex: ["borrower", "first_name"],
                    render: (_, record) =>
                      record.borrower ? `${record.borrower.first_name} ${record.borrower.last_name}` : "N/A",
                  },
                  {
                    title: "Amount (ZMW)",
                    dataIndex: "amount",
                    render: (value) => value.toLocaleString(),
                    sorter: (a, b) => a.amount - b.amount,
                  },
                  {
                    title: "Application Date",
                    dataIndex: "date_created",
                    render: (date) => moment(date).format("DD MMM YYYY"),
                    sorter: (a, b) => moment(a.date_created).unix() - moment(b.date_created).unix(),
                  },
                  {
                    title: "Loan Type",
                    dataIndex: ["loan_product", "loan_name"],
                    render: (_, record) => record.loan_product?.loan_name || "N/A",
                  },
                  {
                    title: "Status",
                    dataIndex: "loan_status",
                    render: (status) => (
                      <Tag color={statusMap[status]?.color || 'default'}>
                        {statusMap[status]?.text || status}
                      </Tag>
                    ),
                    filters: [1, 2, 3].map(key => ({
                      text: statusMap[key]?.text || key,
                      value: key,
                    })),
                    onFilter: (value, record) => record.loan_status === value,
                  },
                ]}
                dataSource={portfolioData.loanGroups.pending}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50"],
                }}
              />
            </TabPane>
            <TabPane tab={`App (${portfolioData.statusDistribution.app})`} key="app">
              <Table
                columns={[
                  { title: "Loan ID", dataIndex: "id", key: "id" },
                  {
                    title: "Borrower",
                    dataIndex: ["borrower", "first_name"],
                    render: (_, record) =>
                      record.borrower ? `${record.borrower.first_name} ${record.borrower.last_name}` : "N/A",
                  },
                  {
                    title: "Amount (ZMW)",
                    dataIndex: "amount",
                    render: (value) => value.toLocaleString(),
                    sorter: (a, b) => a.amount - b.amount,
                  },
                  {
                    title: "Application Date",
                    dataIndex: "date_created",
                    render: (date) => moment(date).format("DD MMM YYYY"),
                    sorter: (a, b) => moment(a.date_created).unix() - moment(b.date_created).unix(),
                  },
                  {
                    title: "Loan Type",
                    dataIndex: ["loan_product", "loan_name"],
                    render: (_, record) => record.loan_product?.loan_name || "N/A",
                  },
                  {
                    title: "Status",
                    dataIndex: "loan_status",
                    render: (status) => (
                      <Tag color={statusMap[status]?.color || 'default'}>
                        {statusMap[status]?.text || status}
                      </Tag>
                    ),
                  },
                ]}
                dataSource={portfolioData.loanGroups.app}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50"],
                }}
              />
            </TabPane>
          </Tabs>
        )}

        {/* Recent Loans Table for Summary View */}
        {filters.viewMode === "summary" && (
          <Card
            title={
              <Title level={4} style={{ margin: 0, color: "#555555" }}>
                Recent Loans
              </Title>
            }
            bordered={false}
            style={{ 
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
              borderRadius: "8px",
            }}
            headStyle={{ 
              backgroundColor: '#f7f7f7', 
              borderBottom: '1px solid #eaeaea'
            }}
          >
            <Table
              columns={[
                { title: "Loan ID", dataIndex: "id", key: "id" },
                {
                  title: "Borrower",
                  dataIndex: ["borrower", "first_name"],
                  render: (_, record) =>
                    record.borrower ? `${record.borrower.first_name} ${record.borrower.last_name}` : "N/A",
                },
                {
                  title: "Amount (ZMW)",
                  dataIndex: "amount",
                  render: (value) => value.toLocaleString(),
                },
                {
                  title: "Disbursement Date",
                  dataIndex: "disbursement_date",
                  render: (date) => moment(date).format("DD MMM YYYY"),
                },
                {
                  title: "Status",
                  dataIndex: "loan_status",
                  render: (status) => (
                    <Tag color={statusMap[status]?.color || 'default'} style={{ opacity: 0.85 }}>
                      {statusMap[status]?.text || status}
                    </Tag>
                  ),
                },
              ]}
              dataSource={portfolioData.recentLoans}
              rowKey="id"
              pagination={false}
              size="middle"
            />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button 
                onClick={() => setFilters({ ...filters, viewMode: 'detailed' })}
                style={{ 
                  border: '1px solid #e0e0e0', 
                  backgroundColor: '#fafafa',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)'
                }}
              >
                View All Loans
              </Button>
            </div>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default LoanPortfolioSummary;
